'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const relatedServices = [
  { label: 'Container Transport', href: '/container-transport' },
  { label: 'Less Container Load (LCL)', href: '/lcl-transport' },
  { label: 'Inland Transport', href: '/inland-transport' },
  { label: 'Customs Representation', href: '/customs-representation' },
  { label: 'Port Operations', href: '/port-operations' },
  { label: 'Cargo Insurance', href: '/cargo-insurance' },
  { label: 'Bank Guarantee', href: '/bank-guarantee' },
];

export default function AirfreightTransportPage() {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F4F6F9' }}>
      <Header />
      <main className="flex-1 pt-32 pb-20">
        {/* Hero section */}
        <section className="max-w-6xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Illustration */}
            <div className="flex items-center justify-center">
              <svg viewBox="0 0 320 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-sm lg:max-w-md" aria-label="Airfreight transport illustration">
                <path d="M40 150 Q80 130 160 128 Q220 126 270 140 Q290 146 285 155 Q280 164 260 162 Q200 158 160 160 Q100 162 60 170 Z" stroke="#1B6B8A" strokeWidth="4" fill="rgba(27,107,138,0.06)" strokeLinejoin="round"/>
                <path d="M270 140 Q300 148 295 155 Q290 162 260 162" stroke="#1B6B8A" strokeWidth="4" fill="none" strokeLinejoin="round"/>
                <path d="M140 130 L100 80 L80 84 L120 138" stroke="#1B6B8A" strokeWidth="4" fill="rgba(27,107,138,0.08)" strokeLinejoin="round"/>
                <path d="M140 158 L100 208 L80 204 L120 152" stroke="#1B6B8A" strokeWidth="4" fill="rgba(27,107,138,0.08)" strokeLinejoin="round"/>
                <path d="M60 148 L38 118 L28 122 L48 152" stroke="#1B6B8A" strokeWidth="3.5" fill="rgba(27,107,138,0.08)" strokeLinejoin="round"/>
                <path d="M60 162 L38 192 L28 188 L48 158" stroke="#1B6B8A" strokeWidth="3.5" fill="rgba(27,107,138,0.08)" strokeLinejoin="round"/>
                <path d="M52 150 L30 100 L42 98 L60 148" stroke="#1B6B8A" strokeWidth="3.5" fill="rgba(27,107,138,0.08)" strokeLinejoin="round"/>
                <circle cx="200" cy="144" r="5" stroke="#1B6B8A" strokeWidth="2.5" fill="none"/>
                <circle cx="220" cy="143" r="5" stroke="#1B6B8A" strokeWidth="2.5" fill="none"/>
                <circle cx="240" cy="143" r="5" stroke="#1B6B8A" strokeWidth="2.5" fill="none"/>
                <ellipse cx="130" cy="138" rx="14" ry="8" stroke="#1B6B8A" strokeWidth="3" fill="rgba(27,107,138,0.1)"/>
                <path d="M20 155 L50 155" stroke="#2A8FAB" strokeWidth="2.5" strokeLinecap="round"/>
                <path d="M10 165 L45 165" stroke="#2A8FAB" strokeWidth="2" strokeLinecap="round"/>
                <path d="M15 145 L42 145" stroke="#2A8FAB" strokeWidth="2" strokeLinecap="round"/>
                <path d="M220 80 Q228 68 240 72 Q244 60 258 64 Q270 58 274 70 Q284 68 284 80 Q284 90 272 90 L228 90 Q216 90 220 80 Z" stroke="#2A8FAB" strokeWidth="2.5" fill="rgba(42,143,171,0.08)"/>
              </svg>
            </div>

            {/* Text */}
            <div>
              <div className="w-10 h-1 mb-5 rounded" style={{ background: '#1B6B8A' }} />
              <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={{ color: '#0F2340', fontFamily: 'var(--font-display, sans-serif)' }}>
                Airfreight transport
              </h1>
              <div className="space-y-5 text-base lg:text-lg leading-relaxed" style={{ color: '#3A5070' }}>
                <p>World Transport Overseas provides comprehensive airfreight solutions for time-critical and high-value shipments. Our <strong style={{ color: '#0F2340' }}>airfreight services</strong> connect businesses to over 200 airports worldwide through strategic partnerships with major airlines, ensuring your cargo reaches its destination with speed and precision.</p>
                <p>Whether you need express next-flight-out delivery or cost-optimised standard air cargo, our experienced team handles every aspect of your shipment — from booking and documentation to customs clearance and final delivery. We specialise in handling fragile, temperature-sensitive, and high-value goods with the utmost care.</p>
                <p>Our global network of agents and own offices ensures seamless coordination at both origin and destination, providing real-time tracking and proactive communication throughout the entire journey. WTO's airfreight team is available around the clock to respond to urgent requirements and last-minute bookings.</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link href="/transport-quote" className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-white font-semibold text-sm transition-all duration-200 hover:opacity-90 shadow-md" style={{ background: '#1A5FAB' }}>
                  Get a Quote
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Key highlights */}
        <section className="py-14" style={{ background: '#0F2340' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-10" style={{ color: '#FFFFFF' }}>Our Airfreight Capabilities</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: 'Express Delivery', desc: 'Time-critical shipments delivered within 24–72 hours to major global destinations' },
                { title: 'Global Network', desc: 'Direct connections to over 200 airports worldwide through major airline partnerships' },
                { title: 'Secure Handling', desc: 'Specialized handling for high-value, fragile, and temperature-sensitive cargo' },
                { title: 'Full Tracking', desc: 'Real-time shipment visibility from origin to destination with live status updates' },
              ]?.map((h, i) => (
                <div key={i} className="rounded-2xl p-6" style={{ background: '#132848', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="w-8 h-8 rounded-lg mb-4 flex items-center justify-center" style={{ background: 'rgba(126,200,220,0.2)' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#1B6B8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M9 12l2 2 4-4M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c1.66 0 3.21.45 4.55 1.23" /></svg>
                  </div>
                  <p className="font-bold text-base mb-1" style={{ color: '#FFFFFF' }}>{h?.title}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#B8CDE0' }}>{h?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service types */}
        <section className="py-14" style={{ background: '#0A1628' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-10" style={{ color: '#FFFFFF' }}>Airfreight Service Types</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { name: 'Express Air Freight', desc: 'Priority door-to-door service for urgent shipments. Fastest transit times with dedicated handling and next-flight-out options.' },
                { name: 'Standard Air Freight', desc: 'Cost-optimised air cargo for non-urgent shipments. Reliable transit times with full tracking and professional handling.' },
                { name: 'Charter Flights', desc: 'Dedicated aircraft for oversized, high-value, or time-critical cargo that cannot be accommodated on scheduled services.' },
                { name: 'Dangerous Goods', desc: 'IATA-certified handling of hazardous materials with full compliance documentation and specialist packaging.' },
              ]?.map((c, i) => (
                <div key={i} className="rounded-2xl p-6" style={{ background: '#132848', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <p className="font-bold text-base mb-2" style={{ color: '#FFFFFF' }}>{c?.name}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#B8CDE0' }}>{c?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related services */}
        <section className="py-14" style={{ background: '#0E1F38' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-8" style={{ color: '#FFFFFF' }}>Other Services</h2>
            <div className="flex flex-wrap gap-3">
              {relatedServices?.map((s) => (
                <Link key={s?.href} href={s?.href} className="px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 hover:opacity-80" style={{ background: '#132848', color: '#B8CDE0', border: '1px solid rgba(255,255,255,0.15)' }}>
                  {s?.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
